import { useEffect, useState } from "react";
import api from "../api";
import { useLang, fmtPrice } from "../i18n";
import Logo from "../components/Logo";
import { Link } from "react-router-dom";

const EMPTY = {
  slug: "",
  title_fr: "",
  title_en: "",
  year: 2026,
  technique_fr: "",
  technique_en: "",
  support_fr: "Toile",
  support_en: "Canvas",
  width_cm: 0,
  height_cm: 0,
  dimensions: "",
  series: "partie-1",
  series_fr: "Partie .I",
  series_en: "Part .I",
  image: "/artworks/p2.jpg",
  price_eur: 0,
  status: "available",
  featured: false,
  sort: 99,
};

const SERIES = [
  ["partie-1", "Partie .I", "Part .I"],
  ["partie-2", "Partie .II", "Part .II"],
];

function Login({ onOk }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("md-token", data.token);
      onOk();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Erreur de connexion");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#141414" }} data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm p-10" style={{ background: "#FAF9F6" }} data-testid="admin-login-form">
        <Logo />
        <h1 className="font-serif-display text-3xl mt-8">{t("admin.login_title")}</h1>
        <p className="font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#8C8983] mt-2 mb-8">{t("admin.login_sub")}</p>
        <input
          data-testid="admin-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mail"
          className="w-full bg-transparent border-b border-[#C5C0B7] py-3 text-sm mb-6"
        />
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("admin.password")}
          className="w-full bg-transparent border-b border-[#C5C0B7] py-3 text-sm mb-8"
        />
        {error && (
          <p className="text-xs text-[#C83E2B] mb-4" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          className="w-full py-4 font-mono-meta text-xs uppercase tracking-[0.2em] text-[#FAF9F6]"
          style={{ background: "#121212" }}
        >
          {t("admin.signin")}
        </button>
        <Link to="/" className="block text-center mt-6 font-mono-meta text-[10px] uppercase tracking-[0.18em] text-[#8C8983] hover:text-[#121212]">
          ← maxdhum
        </Link>
      </form>
    </div>
  );
}

function WorkForm({ work, onSave, onCancel }) {
  const { t } = useLang();
  const [f, setF] = useState(work);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full bg-transparent border-b border-[#C5C0B7] py-2 text-sm";
  const lbl = "font-mono-meta text-[10px] uppercase tracking-[0.15em] text-[#8C8983]";

  const field = (key, label, type = "text") => (
    <label className="block">
      <span className={lbl}>{label}</span>
      <input
        data-testid={`work-field-${key}`}
        type={type}
        value={f[key]}
        onChange={(e) => set(key, type === "number" ? Number(e.target.value) : e.target.value)}
        className={inp}
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: "rgba(18,18,18,0.55)" }} data-testid="work-form-modal">
      <div className="max-w-2xl mx-auto my-10 p-10" style={{ background: "#FAF9F6" }}>
        <h2 className="font-serif-display text-3xl mb-8">{f.id ? t("admin.edit") : t("admin.add")}</h2>
        <div className="grid grid-cols-2 gap-x-8 gap-y-5">
          {field("title_fr", "Titre FR")}
          {field("title_en", "Title EN")}
          {field("technique_fr", "Technique FR")}
          {field("technique_en", "Technique EN")}
          {field("support_fr", "Support FR")}
          {field("support_en", "Support EN")}
          {field("dimensions", "Dimensions")}
          {field("year", "Année", "number")}
          {field("price_eur", "Prix €", "number")}
          {field("sort", "Ordre", "number")}
          {field("image", "Image (/artworks/...)")}
          {field("slug", "Slug")}
          <label className="block">
            <span className={lbl}>Série</span>
            <select
              data-testid="work-field-series"
              value={f.series}
              onChange={(e) => {
                const s = SERIES.find(([k]) => k === e.target.value);
                setF((p) => ({ ...p, series: s[0], series_fr: s[1], series_en: s[2] }));
              }}
              className={`${inp} bg-transparent`}
            >
              {SERIES.map(([k, fr]) => (
                <option key={k} value={k}>
                  {fr}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={lbl}>Statut</span>
            <select
              data-testid="work-field-status"
              value={f.status}
              onChange={(e) => set("status", e.target.value)}
              className={`${inp} bg-transparent`}
            >
              <option value="available">Disponible</option>
              <option value="sold">Vendue (prix masqué)</option>
              <option value="private">Collection privée</option>
            </select>
          </label>
          <label className="flex items-center gap-3 pt-5">
            <input
              data-testid="work-field-featured"
              type="checkbox"
              checked={f.featured}
              onChange={(e) => set("featured", e.target.checked)}
            />
            <span className={lbl}>À la une</span>
          </label>
        </div>
        <div className="flex gap-4 mt-10">
          <button
            data-testid="work-form-save"
            onClick={() => onSave(f)}
            className="px-8 py-3 font-mono-meta text-xs uppercase tracking-[0.18em] text-[#FAF9F6]"
            style={{ background: "#121212" }}
          >
            {t("admin.save")}
          </button>
          <button
            data-testid="work-form-cancel"
            onClick={onCancel}
            className="px-8 py-3 font-mono-meta text-xs uppercase tracking-[0.18em] border border-[#C5C0B7]"
          >
            {t("admin.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const { t, lang } = useLang();
  const [tab, setTab] = useState("works");
  const [works, setWorks] = useState([]);
  const [content, setContent] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = () => {
    api.get("/artworks").then((r) => setWorks(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveWork = async (f) => {
    const payload = { ...f };
    delete payload.id;
    if (f.id) await api.put(`/admin/artworks/${f.id}`, payload);
    else await api.post("/admin/artworks", payload);
    setEditing(null);
    load();
  };

  const removeWork = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/admin/artworks/${id}`);
    load();
  };

  const saveContent = async () => {
    await api.put("/admin/content", content);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    ["works", t("admin.works")],
    ["texts", t("admin.texts")],
    ["enquiries", t("admin.enquiries")],
  ];

  return (
    <div className="min-h-screen" style={{ background: "#F3F1EC" }} data-testid="admin-dashboard">
      <div className="px-6 sm:px-12 py-5 flex items-center justify-between border-b" style={{ borderColor: "#E5E2DC", background: "#FAF9F6" }}>
        <Logo />
        <div className="flex items-center gap-6">
          {tabs.map(([k, label]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`font-mono-meta text-[11px] uppercase tracking-[0.18em] ${tab === k ? "text-[#C83E2B]" : "text-[#8C8983] hover:text-[#121212]"}`}
            >
              {label}
              {k === "enquiries" && enquiries.length > 0 && <span className="ml-1">({enquiries.length})</span>}
            </button>
          ))}
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="font-mono-meta text-[11px] uppercase tracking-[0.18em] border border-[#C5C0B7] px-4 py-2 hover:bg-[#121212] hover:text-[#FAF9F6] transition-colors"
          >
            {t("admin.logout")}
          </button>
        </div>
      </div>

      <div className="px-6 sm:px-12 py-10">
        {tab === "works" && (
          <div>
            <button
              data-testid="admin-add-work-btn"
              onClick={() => setEditing({ ...EMPTY })}
              className="mb-8 px-6 py-3 font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#FAF9F6]"
              style={{ background: "#C83E2B" }}
            >
              + {t("admin.add")}
            </button>
            <div className="border-t" style={{ borderColor: "#E5E2DC" }} data-testid="admin-works-table">
              {works.map((w) => (
                <div key={w.id} className="flex items-center gap-6 py-3 border-b" style={{ borderColor: "#E5E2DC" }}>
                  <img src={w.image} alt="" className="w-12 h-12 object-cover" />
                  <span className="font-serif-display italic text-lg w-48 truncate">{lang === "fr" ? w.title_fr : w.title_en}</span>
                  <span className="font-mono-meta text-[10px] uppercase tracking-[0.12em] text-[#8C8983] w-32">{w.series_fr}</span>
                  <span className="font-mono-meta text-[10px] text-[#8C8983] w-24">{w.dimensions}</span>
                  <span className="text-sm w-24">{fmtPrice(w.price_eur, lang)}</span>
                  <span
                    className="font-mono-meta text-[10px] uppercase tracking-[0.12em]"
                    style={{ color: w.status === "available" ? "#121212" : "#C83E2B" }}
                  >
                    {w.status === "available" ? "Dispo" : w.status === "sold" ? "Vendue" : "Privée"}
                  </span>
                  <span className="ml-auto flex gap-3">
                    <button data-testid={`admin-edit-${w.slug}`} onClick={() => setEditing(w)} className="font-mono-meta text-[10px] uppercase tracking-[0.12em] link-line">
                      {t("admin.edit")}
                    </button>
                    <button data-testid={`admin-delete-${w.slug}`} onClick={() => removeWork(w.id)} className="font-mono-meta text-[10px] uppercase tracking-[0.12em] text-[#C83E2B] link-line">
                      {t("admin.delete")}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "texts" && content && (
          <div className="max-w-3xl space-y-8" data-testid="admin-texts-form">
            {[
              ["bio_fr", "Biographie FR"],
              ["bio_en", "Biography EN"],
              ["quote_fr", "Citation FR"],
              ["quote_en", "Quote EN"],
            ].map(([k, label]) => (
              <label key={k} className="block">
                <span className="font-mono-meta text-[10px] uppercase tracking-[0.15em] text-[#8C8983]">{label}</span>
                <textarea
                  data-testid={`content-field-${k}`}
                  rows={k.startsWith("bio") ? 8 : 2}
                  value={content[k]}
                  onChange={(e) => setContent({ ...content, [k]: e.target.value })}
                  className="w-full mt-2 bg-white border border-[#E5E2DC] p-4 text-sm leading-relaxed"
                />
              </label>
            ))}
            {content.pillars.map((p, i) => (
              <div key={p.key} className="grid grid-cols-1 gap-4 p-6 bg-white border" style={{ borderColor: "#E5E2DC" }}>
                <p className="font-serif-display italic text-xl">{p.title_fr}</p>
                <textarea
                  data-testid={`pillar-text-fr-${i}`}
                  rows={5}
                  value={p.text_fr}
                  onChange={(e) => {
                    const pillars = [...content.pillars];
                    pillars[i] = { ...p, text_fr: e.target.value };
                    setContent({ ...content, pillars });
                  }}
                  className="w-full border border-[#E5E2DC] p-3 text-sm"
                />
                <textarea
                  data-testid={`pillar-text-en-${i}`}
                  rows={5}
                  value={p.text_en}
                  onChange={(e) => {
                    const pillars = [...content.pillars];
                    pillars[i] = { ...p, text_en: e.target.value };
                    setContent({ ...content, pillars });
                  }}
                  className="w-full border border-[#E5E2DC] p-3 text-sm"
                />
              </div>
            ))}
            <button
              data-testid="content-save-btn"
              onClick={saveContent}
              className="px-8 py-3 font-mono-meta text-xs uppercase tracking-[0.18em] text-[#FAF9F6]"
              style={{ background: "#121212" }}
            >
              {saved ? `✓ ${t("admin.saved")}` : t("admin.save")}
            </button>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-list">
            {enquiries.length === 0 && <p className="text-sm text-[#8C8983]">{t("admin.none")}</p>}
            {enquiries.map((e) => (
              <div key={e.id} className="py-4 border-b flex flex-wrap gap-x-8 gap-y-1" style={{ borderColor: "#E5E2DC" }}>
                <span className="font-serif-display italic text-lg">{e.artwork_title}</span>
                <span className="text-sm">{e.name}</span>
                <a href={`mailto:${e.email}`} className="text-sm text-[#C83E2B] link-line">
                  {e.email}
                </a>
                <span className="font-mono-meta text-[10px] text-[#8C8983]">{new Date(e.created_at).toLocaleDateString("fr-FR")}</span>
                {e.message && <span className="w-full text-sm text-[#5A5853]">{e.message}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <WorkForm work={editing} onSave={saveWork} onCancel={() => setEditing(null)} />}
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("md-token")) return setAuth(false);
    api
      .get("/auth/me")
      .then(() => setAuth(true))
      .catch(() => {
        localStorage.removeItem("md-token");
        setAuth(false);
      });
  }, []);

  if (auth === null) return null;
  if (!auth) return <Login onOk={() => setAuth(true)} />;
  return (
    <Dashboard
      onLogout={() => {
        localStorage.removeItem("md-token");
        setAuth(false);
      }}
    />
  );
}
